import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [trialStartDate, setTrialStartDate] = useState(null);
  const [trialEndDate, setTrialEndDate] = useState(null);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(null);
  const [subscriptionStartDate, setSubscriptionStartDate] = useState(null);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState(null);
  const [subscriptionPlan, setSubscriptionPlan] = useState(null);
  const [pendingPlanType, setPendingPlanType] = useState(null);
  const [pendingPlanEffectiveAt, setPendingPlanEffectiveAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkSubscriptionStatus = useCallback(async () => {
    if (!user) {
      setSubscriptionStatus(null);
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/api/subscriptions/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      setSubscriptionStatus(data.subscriptionStatus);
      setTrialStartDate(data.trialStartDate);
      setTrialEndDate(data.trialEndDate);
      setTrialDaysRemaining(data.trialDaysRemaining);
      setSubscriptionStartDate(data.subscriptionStartDate);
      setSubscriptionEndDate(data.subscriptionEndDate);
      setSubscriptionPlan(data.subscriptionPlan);
      setPendingPlanType(data.pendingPlanType ?? null);
      setPendingPlanEffectiveAt(data.pendingPlanEffectiveAt ?? null);
      setError(null);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      setError(error.response?.data?.message || 'Failed to fetch subscription status');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      checkSubscriptionStatus();
    } else {
      setSubscriptionStatus(null);
      setLoading(false);
    }
  }, [user, checkSubscriptionStatus]);

  const hasActiveSubscription = useMemo(() => {
    if (!subscriptionStatus) return false;
    
    // User has access if in trial or active subscription
    return subscriptionStatus === 'trial' || 
           subscriptionStatus === 'active' || 
           subscriptionStatus === 'canceled'; // Canceled but still has access until period end
  }, [subscriptionStatus]);

  const value = useMemo(() => ({
    subscriptionStatus,
    trialStartDate,
    trialEndDate,
    trialDaysRemaining,
    subscriptionStartDate,
    subscriptionEndDate,
    subscriptionPlan,
    pendingPlanType,
    pendingPlanEffectiveAt,
    loading,
    error,
    hasActiveSubscription,
    checkSubscriptionStatus,
  }), [
    subscriptionStatus,
    trialStartDate,
    trialEndDate,
    trialDaysRemaining,
    subscriptionStartDate,
    subscriptionEndDate,
    subscriptionPlan,
    pendingPlanType,
    pendingPlanEffectiveAt,
    loading,
    error,
    hasActiveSubscription,
    checkSubscriptionStatus,
  ]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
